import React from 'react';
import { Route } from 'react-router-dom';
import { Container, Row, Col, Alert, Button, Spinner } from 'react-bootstrap';
import useSkiSaleConfig from '../hooks/useSkiSaleConfig';
import ConsignmentOver from '../ConsignmentOver';

/**
 * Protected route component that conditionally renders based on ski sale status
 * Blocks access when the sale is closed or in maintenance mode
 */
const ProtectedRoute = ({ component: Component, render, adminOnly = false, ...rest }) => {
  const { 
    status, 
    loading, 
    error, 
    isOpen, 
    getStatusMessage, 
    getStatusColor,
    refresh 
  } = useSkiSaleConfig();

  return (
    <Route 
      {...rest} 
      render={(props) => {
        // Check if user is admin (if adminOnly is true)
        const auth = JSON.parse(localStorage.getItem("auth") || '{}');
        const isAdmin = auth && auth.isAdmin;
        
        // Admin users can always access if adminOnly is set
        if (adminOnly && isAdmin) {
          return Component ? <Component {...props} /> : render(props);
        }

        // Admin users can access even when closed/maintenance
        if (isAdmin && !isOpen) {
          return (
            <Container className="mt-4">
              <Alert variant="info" className="mb-3">
                <strong>Admin Access:</strong> You are accessing this page as an administrator 
                while the ski sale is {status}. Regular users cannot access this page at this time.
              </Alert>
              {Component ? <Component {...props} /> : render(props)}
            </Container>
          );
        }

        // Loading state
        if (loading) {
          return (
            <Container className="mt-4">
              <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6}>
                  <div className="text-center">
                    <Spinner animation="border" role="status" variant="primary" size="lg">
                      <span className="sr-only">Loading...</span>
                    </Spinner>
                  </div>
                </Col>
              </Row>
            </Container>
          );
        }

        // Error state - allow access but show warning
        if (error) {
          return (
            <Container className="mt-4">
              <Alert variant="warning" className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Status Check Failed:</strong> Unable to verify ski sale status. 
                    Proceeding with limited functionality.
                  </div>
                  <Button variant="outline-warning" size="sm" onClick={refresh}>
                    Retry
                  </Button>
                </div>
              </Alert>
              {Component ? <Component {...props} /> : render(props)}
            </Container>
          );
        }

        // Closed or maintenance state
        if (!isOpen) {
          if (status === 'closed') {
            // Use the existing ConsignmentOver component for closed state
            return <ConsignmentOver {...props} />;
          }
          
          // Maintenance state - show maintenance message
          return (
            <Container className="mt-4">
              <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6}>
                  <Alert variant={getStatusColor()} className="text-center">
                    <h4>System Maintenance</h4>
                    <p className="mb-3">{getStatusMessage()}</p>
                    
                    <div>
                      <p>
                        We're performing maintenance to improve your experience. 
                        Please try again in a few minutes.
                      </p>
                    </div>
                    
                    <Button 
                      variant={`outline-${getStatusColor()}`} 
                      onClick={refresh}
                      className="mt-2"
                    >
                      Check Status Again
                    </Button>
                  </Alert>
                </Col>
              </Row>
            </Container>
          );
        }

        // Open state - render component normally
        return Component ? <Component {...props} /> : render(props);
      }}
    />
  );
};

export default ProtectedRoute;
